import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import { USER_COLUMNS } from './util';
import { ToolbarContainer } from 'components/common/Containers';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { ActionButton } from 'components/common/Buttons';
import { Link } from 'react-router-dom';
import { searchAnyFieldFilter } from 'utils/common';
import Title from 'components/common/Title';
import { useBoolFlag } from 'hooks/index';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { useSnackbar } from 'notistack';
import { useSelectionModel } from 'hooks/utils/useSelectionModel';
import { DataGridFilterField } from 'components/common/DataTables';
import {
  useDeleteUserMutation,
  useGetCurrentUserQuery,
  useGetUsersQuery,
} from '../../../store/api/user.api';

function Toolbar({ value, onChange, clearSearch, onDelete }) {
  const onSearchChange = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange],
  );
  return (
    <ToolbarContainer>
      <DataGridFilterField
        variant='standard'
        value={value}
        onChange={onSearchChange}
        placeholder='Search…'
        InputProps={{
          startAdornment: <SearchIcon fontSize='small' />,
          endAdornment: (
            <IconButton
              title='Clear'
              aria-label='Clear'
              size='small'
              style={{ visibility: value ? 'visible' : 'hidden' }}
              onClick={clearSearch}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          ),
        }}
      />
      <Box flexGrow={1} px='16px'>
        <ActionButton
          variant='text'
          onClick={onDelete}
          sx={{ textTransform: 'none' }}
        >
          Delete
        </ActionButton>
      </Box>
      <Button
        component={Link}
        to='/users/create'
        color='primary'
        variant='contained'
      >
        Add new
      </Button>
    </ToolbarContainer>
  );
}

export default function UserManagement() {
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data = [], isLoading } = useGetUsersQuery();
  const [mutateAsync] = useDeleteUserMutation();

  const { enqueueSnackbar } = useSnackbar();
  const {
    isOn: isDeleteCurrentConfirm,
    setOn: showDeleteCurrentConfirm,
    setOff: hideDeleteCurrentConfirm,
  } = useBoolFlag(false);
  const {
    isOn: isDeleteConfirm,
    setOn: showDeleteConfirm,
    setOff: hideDeleteConfirm,
  } = useBoolFlag();
  const rows = useMemo(
    () =>
      data.map((user) => ({ ...user, current: user.id === currentUser.id })),
    [data, currentUser],
  );

  const [searchText, setSearchText] = useState('');
  const filteredRows = useMemo(
    () => (searchText ? rows.filter(searchAnyFieldFilter(searchText)) : rows),
    [rows, searchText],
  );
  const { selectionModel, setSelectionModel, clearSelection, getSelectedIds } =
    useSelectionModel(data);

  const clearSearch = useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  const onDeleteClick = useCallback(() => {
    if (getSelectedIds().includes(currentUser.id)) {
      showDeleteCurrentConfirm();
      return;
    }
    showDeleteConfirm();
  }, [
    currentUser.id,
    getSelectedIds,
    showDeleteConfirm,
    showDeleteCurrentConfirm,
  ]);

  const showConfirmPopup = useCallback(() => {
    hideDeleteCurrentConfirm();
    showDeleteConfirm();
  }, [hideDeleteCurrentConfirm, showDeleteConfirm]);

  const deleteUsers = useCallback(async () => {
    try {
      await mutateAsync(getSelectedIds()).unwrap();
      enqueueSnackbar('Success', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete users', { variant: 'error' });
    }
    hideDeleteConfirm();
  }, [getSelectedIds, hideDeleteConfirm, mutateAsync, enqueueSnackbar]);

  return (
    <Paper sx={{ mx: '32px', p: '16px' }}>
      <Title>Users</Title>
      <DataGrid
        columns={USER_COLUMNS}
        rows={filteredRows}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnResize
        loading={isLoading}
        rowSelectionModel={selectionModel}
        showToolbar
        localeText={{
          noRowsLabel: 'No data to display',
        }}
        onRowSelectionModelChange={setSelectionModel}
        slots={{
          toolbar: Toolbar,
        }}
        slotProps={{
          toolbar: {
            clearSearch,
            value: searchText,
            onChange: setSearchText,
            onDelete: onDeleteClick,
          },
        }}
      />
      <ConfirmationDialog
        isOpen={isDeleteCurrentConfirm}
        handleClose={hideDeleteCurrentConfirm}
        handleConfirm={showConfirmPopup}
        contentText={
          <span>
            <Typography>You're about to delete your active user.</Typography>
            <Typography>Are you sure?</Typography>
          </span>
        }
        title={<Typography variant='h5'>Warning</Typography>}
        approveLabel='Proceed'
      />
      <ConfirmationDialog
        isOpen={isDeleteConfirm}
        handleClose={hideDeleteConfirm}
        handleConfirm={deleteUsers}
        contentText={
          <span>
            <Typography>You're about to delete user(s).</Typography>
            <Typography fontWeight={500}>
              This action is irreversible.
            </Typography>
            <Typography>Are you sure?</Typography>
          </span>
        }
        title={<Typography variant='h5'>Confirm action</Typography>}
        approveLabel='Proceed'
      />
    </Paper>
  );
}
